import { Button as AquiloButton, useSnackBar } from "@truevault/aquilo";
import {
  ArchiveBoxArrowDownIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  PaperClipIcon,
  PlusCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  LoadingPageWrapper,
  StandardPageWrapper,
} from "../../components/layout/StandardPageWrapper";
import { PageHeader } from "../../components/layout/header/Header";
import { PageHeaderBack } from "../../components/layout/header/PageHeaderBack";
import { useOrganization, usePrimaryOrganizationId } from "../../hooks";
import {
  usePrivacyEmailConversationDetail,
  usePrivacyEmailReply,
  usePrivacyEmailBulkAction,
} from "../../hooks/usePrivacyEmails";
import { RouteHelpers, Routes } from "../../root/routes";
import { fmtDate } from "../../formatters/date";
import {
  PostmarkOrganizationMessageDto,
  PostmarkOutboundAttachmentDto,
} from "../../../common/service/server/dto/PostmarkInboxDto";
import { canUsePrivacyEmailInbox } from "../../util/privacyEmailSetup";
import { Api } from "../../../common/service/Api";
import { downloadUrl } from "../../../common/util/fileDownload";
import { previewFile } from "../../../common/util/filePreview";
import { sanitizeEmailHtml } from "../../utils/sanitizeEmailHtml";
import clsx from "clsx";
import {
  NewRequestDialog,
  NewRequestDialogPrefill,
} from "../../components/dialog/NewRequestDialog";
import { WYSIWYGEditor } from "../../../common/components/input/WYSIWYGEditor";
import {
  ATTACHMENT_FILE_INPUT_ACCEPT,
  ATTACHMENT_MAX_FILE_SIZE_ERROR,
  ATTACHMENT_UNSUPPORTED_TYPE_ERROR,
  MAX_ATTACHMENT_FILE_BYTES,
  isAllowedAttachmentExtension,
} from "../../constants/attachmentConstraints";

type Params = {
  conversationId: string;
};

const MESSAGE_PAGE_SIZE = 50;
const MAX_ATTACHMENT_COUNT = 10;
const MAX_TOTAL_ATTACHMENT_BYTES = 10 * 1024 * 1024;
type PrivacyEmailConversationLocationState = {
  returnToInboxUrl?: string;
};

type HeaderActionButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: React.ReactNode;
  label: string;
};

const HeaderActionButton = ({ icon, label, className, ...props }: HeaderActionButtonProps) => {
  const renderedIcon = React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
        className: clsx("h-4 w-4", icon.props?.className),
      })
    : icon;

  return (
    <button
      type="button"
      {...props}
      className={clsx(
        "inline-flex items-center gap-1 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2",
        className,
      )}
    >
      <span className="text-neutral-500">{renderedIcon}</span>
      <span>{label}</span>
    </button>
  );
};

const parseParticipantName = (
  displayName: string | undefined,
): { firstName?: string; lastName?: string } => {
  if (!displayName) {
    return {};
  }

  const trimmed = displayName.trim();
  if (!trimmed) {
    return {};
  }

  if (trimmed.includes(",")) {
    const [lastSegment, rest] = trimmed.split(",", 2);
    const restParts = rest?.trim().split(/\s+/).filter(Boolean) ?? [];
    const firstName = restParts.shift();
    const lastNameSegments = [lastSegment.trim(), ...restParts].filter(Boolean);

    return {
      firstName,
      lastName: lastNameSegments.length > 0 ? lastNameSegments.join(" ") : undefined,
    };
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: parts[0] };
  }

  const firstName = parts.shift();
  const lastName = parts.length ? parts.join(" ") : undefined;
  return { firstName, lastName };
};

const htmlToPlainText = (html: string): string => {
  if (!html) return "";

  if (typeof window === "undefined") {
    return html
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  const temp = window.document.createElement("div");
  temp.innerHTML = html;

  temp.querySelectorAll("blockquote").forEach((blockquote) => {
    const quoteText = blockquote.textContent ?? "";
    const lines = quoteText
      .split(/\r?\n/)
      .map((line) => (line.trim().length > 0 ? `> ${line.trim()}` : ">"));
    const replacement = window.document.createTextNode(`\n${lines.join("\n")}\n`);
    blockquote.replaceWith(replacement);
  });

  temp.querySelectorAll("br").forEach((br) => {
    br.replaceWith(window.document.createTextNode("\n"));
  });

  const text = temp.textContent ?? "";
  return text
    .replace(/\u00a0/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trimEnd();
};

const hasComposerContent = (html: string, text: string): boolean => {
  if (html && html.trim().length > 0) return true;
  if (text && text.trim().length > 0) return true;
  return false;
};

const formatBytes = (bytes: number): string => {
  if (bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** index;
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const stripQuotedEmailHtml = (html: string): string => {
  if (!html) return html;
  if (typeof window === "undefined") return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const selectors = [
      ".gmail_quote",
      ".gmail_attr",
      ".gmail_extra",
      ".gmail_signature",
      ".yahoo_quoted",
      ".moz-cite-prefix",
      "blockquote.gmail_quote",
      "blockquote.quoted-reply",
      "blockquote[type='cite']",
      "blockquote[class*='quoted']",
    ];

    selectors.forEach((selector) => {
      doc.querySelectorAll(selector).forEach((node) => node.remove());
    });

    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const textNode = walker.currentNode as Text;
      const text = textNode.textContent ?? "";
      const match = text.match(/On\s.+wrote:/i);
      if (!match) continue;

      const startOffset = match.index ?? 0;
      const range = doc.createRange();
      range.setStart(textNode, startOffset);
      range.setEndAfter(doc.body.lastChild ?? textNode);
      range.deleteContents();
      range.detach();
      break;
    }

    const stripped = doc.body.innerHTML.trim();
    return stripped.length > 0 ? stripped : "";
  } catch {
    return html;
  }
};

const stripQuotedPlainText = (text: string): string => {
  if (!text) return text;
  const lines = text.split(/\r?\n/);
  const kept: string[] = [];
  for (const line of lines) {
    if (/^On .+ wrote:/i.test(line.trim())) {
      break;
    }
    kept.push(line);
  }
  return kept.join("\n").trimEnd();
};

export const PrivacyEmailConversationPage = () => {
  const { conversationId } = useParams<Params>();
  const history = useHistory();
  const location = useLocation<PrivacyEmailConversationLocationState | undefined>();
  const orgId = usePrimaryOrganizationId();
  const [org, orgRequest] = useOrganization(orgId, true);
  const showPrivacyEmails = canUsePrivacyEmailInbox(org);
  const [bodyHtml, setBodyHtml] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [editorSeedHtml, setEditorSeedHtml] = useState("");
  const [composerEditorKey, setComposerEditorKey] = useState(0);
  const [isComposerVisible, setIsComposerVisible] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [newRequestDialogOpen, setNewRequestDialogOpen] = useState(false);
  const [newRequestPrefill, setNewRequestPrefill] = useState<NewRequestDialogPrefill | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [detailPage, setDetailPage] = useState(0);

  const detailParams = useMemo(() => ({ page: detailPage, per: MESSAGE_PAGE_SIZE }), [detailPage]);

  const {
    result: detail,
    request,
    refresh,
  } = usePrivacyEmailConversationDetail(orgId, conversationId, detailParams, showPrivacyEmails);

  useEffect(() => {
    if (org && !showPrivacyEmails) {
      history.replace(Routes.requests.active);
    }
  }, [org, showPrivacyEmails, history]);

  useEffect(() => {
    setDetailPage(0);
  }, [conversationId]);

  useEffect(() => {
    if (detail?.conversation?.id !== conversationId) {
      return;
    }
    const totalPages = detail?.messages?.meta?.totalPages ?? 0;
    if (detailPage === 0 && totalPages > 1) {
      setDetailPage(totalPages - 1);
    }
  }, [conversationId, detail?.conversation?.id, detail?.messages?.meta?.totalPages, detailPage]);

  const conversation = detail?.conversation;
  const messages = useMemo(() => detail?.messages.data ?? [], [detail?.messages.data]);

  const attachmentsTotalSize = useMemo(
    () => attachments.reduce((acc, file) => acc + file.size, 0),
    [attachments],
  );

  const handleComposerChange = useCallback((contents: string) => {
    setBodyHtml(contents);
    setBodyText(htmlToPlainText(contents));
  }, []);

  const bulkAction = usePrivacyEmailBulkAction(orgId);
  const replyAction = usePrivacyEmailReply(orgId, conversationId);

  const threadIds = useMemo(() => {
    const conversationIdValue = conversation?.id;
    if (!conversationIdValue) return [] as string[];
    const ids = conversation?.threadConversationIds ?? [];
    return Array.from(new Set([conversationIdValue, ...ids].filter(Boolean)));
  }, [conversation?.id, conversation?.threadConversationIds]);

  const handleAttachmentAddClick = () => {
    fileInputRef.current?.click();
  };

  const handleAttachmentInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    const next: File[] = [...attachments];
    let currentTotal = attachmentsTotalSize;
    let added = false;
    let hitCountLimit = false;
    let hitSizeLimit = false;
    let hitPerFileSizeLimit = false;
    let hitUnsupportedType = false;

    for (const file of files) {
      if (!isAllowedAttachmentExtension(file.name)) {
        hitUnsupportedType = true;
        continue;
      }
      if (file.size > MAX_ATTACHMENT_FILE_BYTES) {
        hitPerFileSizeLimit = true;
        continue;
      }
      if (next.length >= MAX_ATTACHMENT_COUNT) {
        hitCountLimit = true;
        break;
      }
      if (currentTotal + file.size > MAX_TOTAL_ATTACHMENT_BYTES) {
        hitSizeLimit = true;
        continue;
      }
      next.push(file);
      currentTotal += file.size;
      added = true;
    }

    if (added) {
      setAttachments(next);
    }

    if (hitCountLimit) {
      showSnackbar(`You can attach at most ${MAX_ATTACHMENT_COUNT} files.`, { variant: "error" });
    } else if (hitPerFileSizeLimit) {
      showSnackbar(ATTACHMENT_MAX_FILE_SIZE_ERROR, { variant: "error" });
    } else if (hitUnsupportedType) {
      showSnackbar(ATTACHMENT_UNSUPPORTED_TYPE_ERROR, { variant: "error" });
    } else if (hitSizeLimit) {
      showSnackbar("Attachments exceed the 10 MB total size limit.", { variant: "error" });
    }

    if (event.target.value) {
      event.target.value = "";
    }
  };

  const handleAttachmentRemove = (index: number) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleAttachmentPreview = (file: File) => {
    try {
      if (!previewFile(file)) {
        showSnackbar("Unable to open attachment preview", { variant: "error" });
      }
    } catch (error) {
      console.error(error);
      showSnackbar("Unable to preview attachment", { variant: "error" });
    }
  };

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== "string") {
          reject(new Error("Failed to read attachment."));
          return;
        }
        const commaIndex = result.indexOf(",");
        const base64 = commaIndex >= 0 ? result.slice(commaIndex + 1) : result;
        resolve(base64.replace(/\s+/g, ""));
      };
      reader.onerror = () => reject(reader.error ?? new Error("Failed to read attachment."));
      reader.readAsDataURL(file);
    });

  const handleSend = async () => {
    const htmlPayload = bodyHtml.trim();
    const textPayload = bodyText.trim() || htmlToPlainText(bodyHtml).trim();

    if (!htmlPayload && !textPayload) {
      return;
    }

    if (
      attachments.length > MAX_ATTACHMENT_COUNT ||
      attachmentsTotalSize > MAX_TOTAL_ATTACHMENT_BYTES
    ) {
      showSnackbar(
        "Attachments exceed Postmark's limits. Please remove some files and try again.",
        {
          variant: "error",
        },
      );
      return;
    }

    let attachmentsPayload: PostmarkOutboundAttachmentDto[] | undefined;

    if (attachments.length > 0) {
      try {
        attachmentsPayload = await Promise.all(
          attachments.map(async (file) => {
            const content = await readFileAsBase64(file);
            const payload: PostmarkOutboundAttachmentDto = {
              fileName: file.name,
              mimeType: file.type || undefined,
              fileSize: file.size,
              content,
            };
            return payload;
          }),
        );
      } catch (error) {
        console.error(error);
        showSnackbar("Unable to read one of the attachments.", { variant: "error" });
        return;
      }
    }

    await replyAction.fetch({
      bodyHtml: htmlPayload || undefined,
      bodyText: textPayload || undefined,
      attachments: attachmentsPayload,
    });
    setBodyHtml("");
    setBodyText("");
    setEditorSeedHtml("");
    setComposerEditorKey((prev) => prev + 1);
    setAttachments([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    await refresh();
  };

  const handleArchive = async () => {
    if (!conversation || threadIds.length === 0) return;
    const action: "ARCHIVE" | "UNARCHIVE" =
      conversation.status === "ARCHIVED" ? "UNARCHIVE" : "ARCHIVE";
    await bulkAction.fetch({ ids: threadIds, action });
    await refresh();
  };

  const handleToggleUnread = async () => {
    if (!conversation || threadIds.length === 0) return;
    const action: "MARK_READ" | "MARK_UNREAD" =
      conversation.unreadCount > 0 ? "MARK_READ" : "MARK_UNREAD";
    await bulkAction.fetch({ ids: threadIds, action });
    await refresh();
  };

  const handleOpenCreateRequest = () => {
    const customerParticipant = conversation?.participantsPreview?.find(
      (participant) => participant.direction === "CUSTOMER",
    );

    const nameFromParticipant = parseParticipantName(customerParticipant?.displayName);
    const nameFromMessage = parseParticipantName(latestInboundMessage?.fromName);

    const emailFromParticipant = customerParticipant?.emailAddress;
    const emailFromMessage = latestInboundMessage?.fromEmail;
    const sanitizedReplyTo =
      replyToAddress && replyToAddress.trim().length > 0 ? replyToAddress : undefined;

    const prefill: NewRequestDialogPrefill = {
      subjectFirstName: nameFromParticipant.firstName ?? nameFromMessage.firstName,
      subjectLastName: nameFromParticipant.lastName ?? nameFromMessage.lastName,
      subjectEmailAddress: emailFromParticipant ?? emailFromMessage ?? sanitizedReplyTo,
      sendEmailVerification: false,
    };

    setNewRequestPrefill(prefill);
    setNewRequestDialogOpen(true);
  };

  const showSnackbar = useSnackBar();

  const latestInboundMessage = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const candidate = messages[index];
      if (candidate.direction === "INBOUND") {
        return candidate;
      }
    }
    return messages[messages.length - 1];
  }, [messages]);

  const replyToAddress =
    latestInboundMessage?.fromEmail ??
    conversation?.participantsPreview?.find((p) => p.direction === "CUSTOMER")?.emailAddress ??
    "";

  const handleAttachmentDownload = async (messageId: string, attachmentId: string) => {
    try {
      const result = await Api.postmarkInbox.downloadAttachment(orgId, messageId, attachmentId);
      if (!result?.url) {
        throw new Error("Attachment download URL missing");
      }
      downloadUrl(result.url);
    } catch (error) {
      console.error(error);
      showSnackbar("Unable to download attachment", { variant: "error" });
    }
  };

  const handleCopyEmail = async (email?: string) => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      showSnackbar("Email copied", { variant: "success" });
    } catch (error) {
      console.error(error);
      showSnackbar("Unable to copy email", { variant: "error" });
    }
  };

  useEffect(() => {
    setIsComposerVisible(false);
  }, [conversationId]);

  useEffect(() => {
    const draftHtml = detail?.replyDraft?.html ?? "";
    const draftText = detail?.replyDraft?.text ?? (draftHtml ? htmlToPlainText(draftHtml) : "");

    setEditorSeedHtml(draftHtml);
    setBodyHtml(draftHtml);
    setBodyText(draftText);
    setComposerEditorKey((prev) => prev + 1);
    setAttachments([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [detail?.replyDraft?.html, detail?.replyDraft?.text, conversationId]);

  const hasUnsavedReplyChanges =
    isComposerVisible && (hasComposerContent(bodyHtml, bodyText) || attachments.length > 0);

  useEffect(() => {
    if (!hasUnsavedReplyChanges) {
      return;
    }

    const warningMessage = "You have an unsaved reply. Are you sure you want to leave?";

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = warningMessage;
      return warningMessage;
    };

    const unblock = history.block(() => warningMessage);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      unblock();
    };
  }, [hasUnsavedReplyChanges, history]);

  if (!org && orgRequest.running) {
    return <LoadingPageWrapper />;
  }

  if (org && !showPrivacyEmails) {
    return <LoadingPageWrapper />;
  }

  if (!detail && request.running) {
    return <LoadingPageWrapper />;
  }

  const pageHeaderTitle = conversation?.subject ?? "Privacy Emails";
  const handleBack = () => {
    const returnToInboxUrl = location.state?.returnToInboxUrl;
    if (returnToInboxUrl) {
      history.push(returnToInboxUrl);
      return;
    }
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.push(RouteHelpers.requests.privacyEmails());
  };

  const headerBackLink = <PageHeaderBack onClick={handleBack}>Back</PageHeaderBack>;
  const handleGoToRequest = () => {
    if (conversation?.primaryRequestPublicId) {
      history.push(RouteHelpers.requests.detail(conversation.primaryRequestPublicId));
    }
  };

  const headerActions = conversation ? (
    <div className="flex items-center gap-xs">
      <HeaderActionButton
        icon={
          <ArchiveBoxArrowDownIcon
            className={conversation.status === "ARCHIVED" ? "transform rotate-180" : undefined}
          />
        }
        label={conversation.status === "ARCHIVED" ? "Unarchive" : "Archive"}
        aria-label={
          conversation.status === "ARCHIVED" ? "Unarchive conversation" : "Archive conversation"
        }
        onClick={handleArchive}
        disabled={bulkAction.request.running}
      />
      <HeaderActionButton
        icon={conversation.unreadCount > 0 ? <EnvelopeOpenIcon /> : <EnvelopeIcon />}
        label={conversation.unreadCount > 0 ? "Mark read" : "Mark unread"}
        aria-label={conversation.unreadCount > 0 ? "Mark as read" : "Mark as unread"}
        onClick={handleToggleUnread}
        disabled={bulkAction.request.running}
      />
      {conversation.primaryRequestPublicId ? (
        <HeaderActionButton
          className="border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100"
          icon={<DocumentTextIcon className="text-primary-700" />}
          label="Go to request"
          aria-label="Go to linked request"
          onClick={handleGoToRequest}
        />
      ) : (
        <HeaderActionButton
          icon={<PlusCircleIcon />}
          label="Create request"
          aria-label="Create data request"
          onClick={handleOpenCreateRequest}
        />
      )}
    </div>
  ) : undefined;

  const contentContainerClass = "max-w-[960px] pr-8";
  const isSendDisabled = !hasComposerContent(bodyHtml, bodyText) || replyAction.request.running;

  if (!conversation) {
    return (
      <StandardPageWrapper fullWidth>
        <div className="tw-boundary">
          <div className={contentContainerClass}>
            <PageHeader
              titleContent={pageHeaderTitle}
              titleHeaderContent={headerBackLink}
              marginBottomPx={24}
            />
            <p className="mt-md text-base text-neutral-700">Conversation not found.</p>
          </div>
        </div>
      </StandardPageWrapper>
    );
  }

  return (
    <StandardPageWrapper fullWidth>
      <div className="tw-boundary">
        <div className={contentContainerClass}>
          <PageHeader
            titleContent={pageHeaderTitle}
            titleHeaderContent={headerBackLink}
            actions={headerActions}
            marginBottomPx={12}
          />

          <div className="flex flex-col gap-lg">
            <section className="bg-white overflow-hidden shadow-[0px_2px_12px_0px_#00000033] border border-[#D1CED9] rounded-[10px]">
              <div className="px-9 py-3">
                <div className="flex flex-col">
                  {messages.map((message, index) => {
                    const isFirst = index === 0;
                    const isLast = index === messages.length - 1;
                    const showCopyButton =
                      isFirst && !!replyToAddress && message.fromEmail === replyToAddress;

                    return (
                      <div
                        key={message.id}
                        className={clsx("py-mdlg", !isLast && "border-b")}
                        style={{
                          borderColor: !isLast ? "#E8E5EE" : undefined,
                          paddingTop: isFirst ? 0 : undefined,
                          paddingBottom: isLast ? 0 : undefined,
                        }}
                      >
                        <MessageBubble
                          message={message}
                          onDownloadAttachment={handleAttachmentDownload}
                          showCopyButton={showCopyButton}
                          onCopyEmail={
                            showCopyButton && replyToAddress
                              ? () => handleCopyEmail(replyToAddress)
                              : undefined
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {isComposerVisible ? (
              <div className="bg-white overflow-hidden shadow-[0px_2px_12px_0px_#00000033] border border-[#D1CED9] rounded-[10px]">
                <div className="flex flex-col gap-0 bg-white p-9 mb-0">
                  <div className="flex items-center text-sm text-neutral-700">
                    <span className="font-medium text-neutral-900 mr-1">To:</span>
                    <span>{replyToAddress || "Select a recipient"}</span>
                  </div>

                  <div className="overflow-hidden bg-white border border-[#D1CED9] rounded-[10px] [&_.rx-editor_h1]:text-[40px] [&_.rx-editor_h1]:leading-[1.2] [&_.rx-editor_h1]:font-bold [&_.rx-editor_h2]:text-[24px] [&_.rx-editor_h2]:leading-[1.3] [&_.rx-editor_h2]:font-bold [&_.rx-editor_h3]:text-[21px] [&_.rx-editor_h3]:leading-[1.5] [&_.rx-editor_h3]:font-bold [&_.rx-editor_h4]:text-[18px] [&_.rx-editor_h4]:leading-[1.5] [&_.rx-editor_h4]:font-bold [&_.rx-editor_h5]:text-[16px] [&_.rx-editor_h5]:leading-[1.5] [&_.rx-editor_h5]:font-bold [&_.rx-editor_h6]:text-[14px] [&_.rx-editor_h6]:leading-[1.5] [&_.rx-editor_h6]:font-bold">
                    <WYSIWYGEditor
                      key={`${conversationId}-${composerEditorKey}`}
                      defaultValue={editorSeedHtml}
                      onChange={handleComposerChange}
                      height="220px"
                      id="privacy-email-composer-editor"
                      name="privacy-email-composer-editor"
                    />
                  </div>

                  <div className="flex flex-col gap-sm mt-sm">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={ATTACHMENT_FILE_INPUT_ACCEPT}
                      hidden
                      onChange={handleAttachmentInputChange}
                    />

                    {attachments.length > 0 && (
                      <div className="flex flex-wrap gap-sm">
                        {attachments.map((file, index) => (
                          <div
                            key={`${file.name}-${index}`}
                            className="inline-flex items-center gap-2 rounded bg-primary-50 px-sm py-1 text-xs font-medium text-primary-700 shadow-sm"
                          >
                            <button
                              type="button"
                              className="inline-flex items-center gap-2 text-inherit"
                              onClick={() => handleAttachmentPreview(file)}
                            >
                              <PaperClipIcon className="h-4 w-4" />
                              <span className="truncate max-w-[180px]">{file.name}</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleAttachmentRemove(index)}
                              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-primary-700 hover:text-primary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
                              aria-label={`Remove attachment ${file.name}`}
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-sm mt-sm">
                    <AquiloButton
                      size="m"
                      color="primary"
                      onClick={handleSend}
                      disabled={isSendDisabled}
                    >
                      Send
                    </AquiloButton>
                    <AquiloButton
                      size="m"
                      color="tertiary"
                      onClick={handleAttachmentAddClick}
                      disabled={
                        attachments.length >= MAX_ATTACHMENT_COUNT ||
                        attachmentsTotalSize >= MAX_TOTAL_ATTACHMENT_BYTES ||
                        replyAction.request.running
                      }
                      type="button"
                    >
                      <PlusCircleIcon className="h-4 w-4 mr-2" />
                      Add attachment
                    </AquiloButton>
                    {attachments.length > 0 && (
                      <span className="text-xs text-neutral-500">
                        {attachments.length} file{attachments.length === 1 ? "" : "s"} ·{" "}
                        {formatBytes(attachmentsTotalSize)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <AquiloButton size="m" color="primary" onClick={() => setIsComposerVisible(true)}>
                  Reply
                </AquiloButton>
              </div>
            )}
          </div>
        </div>
      </div>
      <NewRequestDialog
        open={newRequestDialogOpen}
        prefill={newRequestPrefill}
        postmarkConversationId={conversation?.id}
        onPostmarkSuccess={async () => {
          await refresh();
        }}
        onClose={() => {
          setNewRequestDialogOpen(false);
          setNewRequestPrefill(undefined);
        }}
      />
    </StandardPageWrapper>
  );
};

type MessageBubbleProps = {
  message: PostmarkOrganizationMessageDto;
  onDownloadAttachment: (messageId: string, attachmentId: string) => void;
  showCopyButton?: boolean;
  onCopyEmail?: () => void;
};

const MessageBubble = ({
  message,
  onDownloadAttachment,
  showCopyButton,
  onCopyEmail,
}: MessageBubbleProps) => {
  const timestamp = fmtDate(message.sentAt ?? message.createdAt, "MMM D, YYYY h:mm a");
  const sanitizedHtml = useMemo(() => {
    if (!message.htmlBody) return undefined;
    return sanitizeEmailHtml(message.htmlBody);
  }, [message.htmlBody]);
  const displayHtml = useMemo(() => {
    if (!sanitizedHtml) return undefined;
    const stripped = stripQuotedEmailHtml(sanitizedHtml);
    return stripped.trim().length > 0 ? stripped : undefined;
  }, [sanitizedHtml]);
  const displayText = useMemo(
    () => stripQuotedPlainText(message.textBody ?? ""),
    [message.textBody],
  );

  const senderName = message.fromName?.trim();
  const senderEmail = message.fromEmail?.trim();
  const statusPrefix = message.direction === "OUTBOUND" ? "Sent on:" : "Received on:";

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex flex-wrap items-start justify-between gap-sm text-sm text-neutral-600">
        <div className="flex flex-col gap-0 leading-[20px]">
          {(senderName || !senderEmail) && (
            <div className="text-neutral-600 mb-0">
              <span className="text-neutral-900 font-medium mr-1">From:</span>
              <span className="text-neutral-800">{senderName ?? "Unknown sender"}</span>
            </div>
          )}
          {senderEmail && (
            <div className="flex flex-wrap items-center gap-xs text-neutral-600">
              <span>
                <span className="text-neutral-900 font-medium mr-1">Email:</span>
                <span className="text-neutral-800">{senderEmail}</span>
              </span>
              {showCopyButton && onCopyEmail && (
                <AquiloButton size="xs" color="tertiary" onClick={onCopyEmail}>
                  Copy
                </AquiloButton>
              )}
            </div>
          )}
        </div>
        <div className="text-xs whitespace-nowrap text-[#918CA1]">
          {statusPrefix} {timestamp}
        </div>
      </div>
      <div className="text-sm text-neutral-700 [&_a]:text-primary-600 [&_a]:underline [&_a:hover]:text-primary-700 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_blockquote]:mt-2 [&_blockquote]:border-l-2 [&_blockquote]:border-neutral-200 [&_blockquote]:pl-3 [&_blockquote]:text-neutral-600 [&_h1]:my-3 [&_h1]:text-[40px] [&_h1]:leading-[1.2] [&_h1]:font-bold [&_h2]:my-3 [&_h2]:text-[24px] [&_h2]:leading-[1.3] [&_h2]:font-bold [&_h3]:my-3 [&_h3]:text-[21px] [&_h3]:leading-[1.5] [&_h3]:font-bold [&_h4]:my-2 [&_h4]:text-[18px] [&_h4]:leading-[1.5] [&_h4]:font-bold [&_h5]:my-2 [&_h5]:text-[16px] [&_h5]:leading-[1.5] [&_h5]:font-bold [&_h6]:my-2 [&_h6]:text-[14px] [&_h6]:leading-[1.5] [&_h6]:font-bold [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:pl-6 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:list-outside [&_ul]:pl-6 [&_li]:mb-1 [&_li:last-child]:mb-0">
        {displayHtml ? (
          <div dangerouslySetInnerHTML={{ __html: displayHtml }} />
        ) : (
          <p className="whitespace-pre-wrap text-sm text-neutral-700">
            {displayText || "(No content)"}
          </p>
        )}
      </div>
      {message.attachments.length > 0 && (
        <div className="mt-sm flex flex-wrap gap-sm">
          {message.attachments.map((attachment) => (
            <button
              key={attachment.id}
              type="button"
              className="inline-flex items-center gap-2 rounded bg-primary-50 px-sm py-1 text-xs font-medium text-primary-700 shadow-sm hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
              onClick={() => onDownloadAttachment(message.id, attachment.id)}
            >
              <PaperClipIcon className="h-4 w-4" />
              <span className="truncate max-w-[220px]">{attachment.fileName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PrivacyEmailConversationPage;
